import {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { debounce } from 'lodash';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { platformIconsCircleMapping } from '../../../utils/platforms';
import { ReactComponent as WebIcon } from '../../../assets/webCircle.svg';
import { formatWithCustomGigaBillion } from '../../../utils/fomatNumbers';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';
import { ALL_STATUSES_QUERY } from '../../../utils/actors';

export interface SourceOption {
  value: number;
  label: string;
  icon?: React.ReactNode;
  followers_count?: number;
}

export interface SourcesResponse {
  objects: SourceData[];
  total_count: number;
}

export interface SourceData {
  id: number;
  name: string;
  audience: number;
  source_type: string;
}

export const SourceChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [selectedSources, setSelectedSources] = useState<SourceOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);

  // Use draft filters from context
  const selected = state.draftFilters.sources.value.map((s) => s);

  useEffect(() => {
    let isCancelled = false;

    const fetchSelected = async () => {
      if (state.draftFilters.sources.value.length === 0) {
        if (!isCancelled) {
          setSelectedSources([]);
        }
        return;
      }

      const urlParams = new URLSearchParams();
      state.draftFilters.sources.value.forEach((id: number) => {
        urlParams.append('ids', id.toString());
      });

      try {
        const data: SourcesResponse = await API.fetch('GET', `/API/v1/sources?${urlParams.toString()}`);
        if (!isCancelled) {
          setSelectedSources(
            data.objects.map((source: SourceData) => {
              let followers_cnt = source.audience;
              return {
                value: source.id,
                label: source.name,
                icon: platformIconsCircleMapping[source.source_type] || (
                  <WebIcon />
                ),
                followers_count: followers_cnt || 0,
              };
            }),
          );
        }
      } catch (error) {
        showRequestError(error, t('Sources'));
      }
    };

    fetchSelected();

    return () => {
      isCancelled = true;
    };
  }, [state.draftFilters.sources.value]);

  const getSources = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (!inputValue) {
          callback([]);
          setIsSearching(false);
          return;
        }

        API.fetch(
          'GET',
          `/API/v2/actors?q=${inputValue}&sorting=-relevance&${ALL_STATUSES_QUERY}`,
        ).then((data: SourcesResponse) => {
          let optionsList = data.objects.map((source) => {
            let followers_cnt = source?.audience;

            return {
              value: source.id,
              label: source.name,
              icon: platformIconsCircleMapping[source.source_type] || (
                <WebIcon />
              ),
              followers_count: followers_cnt || 0,
            };
          });
          callback(optionsList);
          setIsSearching(false);
        }).catch((error) => {
          showRequestError(error, t('Sources'));
          setIsSearching(false);
        });
      }, 1000),
    [],
  );

  useEffect(() => {
    return () => {
      getSources.cancel();
    };
  }, [getSources]);

  const handleClearAll = () => {
    updateDraftFilters({
      sources: {
        value: [],
        exclude: false,
      },
    });
  };

  const handleToggleSource = useCallback((sourceId: number) => {
    const currentSources = state.draftFilters.sources.value;
    const sourceIdNum = Number(sourceId);

    if (currentSources.includes(sourceIdNum)) {
      updateDraftFilters({
        sources: {
          value: currentSources.filter((id) => id !== sourceIdNum),
          exclude: state.draftFilters.sources.exclude,
        },
      });
    } else {
      updateDraftFilters({
        sources: {
          value: [...currentSources, sourceIdNum],
          exclude: state.draftFilters.sources.exclude,
        },
      });
    }
  }, [state.draftFilters.sources.value, state.draftFilters.sources.exclude, updateDraftFilters]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value) {
      setIsSearching(true);
    }
    getSources(value, setSources);
  };

  const isNoResults = !isSearching && searchValue && sources.length === 0;

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return sources.map((source: SourceOption) => ({
      name: `${source.label} (${formatWithCustomGigaBillion(source.followers_count)})`,
      icon: source.icon,
      checked: selected.includes(source.value),
      onChange: () => handleToggleSource(source.value),
    }));
  }, [sources, selected, handleToggleSource]);

  // Memoize badges to prevent recreating on every render
  const badges = useMemo(() => {
    return selectedSources.map((source: SourceOption) => ({
      name: source.label,
      onChange: () => handleToggleSource(source.value),
    }));
  }, [selectedSources, handleToggleSource]);

  return (
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search sources')}
          hint={t('Use search to find actors')}
        />
      }
      actionsComponent={
        <ControlsPanel
          id="sources-filter"
          count={state.draftFilters.sources.value.length}
          isExclude={state.draftFilters.sources.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onExcludeClick={() => {
            updateDraftFilters({
              sources: {
                value: state.draftFilters.sources.value,
                exclude: !state.draftFilters.sources.exclude
              }
            });
          }}
        />
      }
      badgesComponent={
        <ItemBadgesContainer badges={badges} />
      }
    >
      {isSearching ? (
        <FilterLoader />
      ) : isNoResults ? (
        <NoResults />
      ) : (
        <CheckboxList items={checkboxItems} />
      )}
    </ContentContainer>
  );
};
